import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { qk } from '@/lib/queryClient'
import { useToast } from './useToast'

export function useAccount() {
  return useQuery({
    queryKey: qk.account,
    queryFn: ({ signal }) => api.account.get(signal),
  })
}

export function useUpdateEmail() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: (email: string) => api.account.update({ email: email.trim() || null }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.account })
      void queryClient.invalidateQueries({ queryKey: qk.me })
      toast.success('Email updated')
    },
    onError: (error) => toast.fromError(error, 'Could not update your email'),
  })
}

export function useChangePassword() {
  const toast = useToast()

  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api.account.changePassword(input),
    onSuccess: () => toast.success('Password changed', 'Use it the next time you sign in.'),
    onError: (error) => toast.fromError(error, 'Could not change your password'),
  })
}

export function useSessions() {
  return useQuery({
    queryKey: qk.sessions,
    queryFn: ({ signal }) => api.auth.sessions(signal),
  })
}

export function useRevokeSession() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: (id: string) => api.auth.revokeSession(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.sessions })
      toast.success('Session signed out')
    },
    onError: (error) => toast.fromError(error, 'Could not revoke that session'),
  })
}

/**
 * The caller's invite link. Created by the server on first read, so this is a
 * plain query rather than a mutation — asking for it is what mints it.
 */
export function useReferral() {
  return useQuery({
    queryKey: qk.referral,
    queryFn: ({ signal }) => api.account.referral(signal),
    staleTime: 60_000,
  })
}
